import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { Button } from "@mui/material";
import { useCreateChatChannelMutation } from "Api/Mutations/Chat/useCreateChatChannelMutation";
import { PhoneVerificationDialog } from "Components/Shared/PhoneVerification/PhoneVerificationDialog";
import { usePhoneVerificationStatus } from "Hooks/usePhoneVerificationStatus";
import {
  ChatChannelEligibility,
  ChatChannelKind,
  type PublicProfileDto,
} from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { useState } from "react";

const RESOURCES = Resources.Social.PublicProfile;

type Props = {
  profile: PublicProfileDto;
  /** Anonymous visitor, or a graph mutation of this profile in flight. */
  isDisabled: boolean;
  /** Wraps the click with the HandleGate (useHandleGate.guarded). */
  guarded: (action: () => void) => () => void;
};

/**
 * "Send message" on the public profile. One member and no name is the backend's direct-message
 * rule, and the create is a GetOrCreate — an existing DM comes back rather than a duplicate, so
 * mutual followers and strangers share this one call.
 *
 * Chat is gated on a verified phone, but that gate lives on the chat PAGE — reaching it by
 * creating the channel first would put a message request in front of the recipient from someone
 * who then cannot type. So the check happens here, before the mutation.
 */
export const SendMessageButton = ({ profile, isDisabled, guarded }: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();
  const [isPhoneGateOpen, setIsPhoneGateOpen] = useState(false);
  const {
    isVerified: isPhoneVerified,
    phone,
    hasPhone,
  } = usePhoneVerificationStatus();

  const { mutate: createChannel, isPending } = useCreateChatChannelMutation({
    // The profile lives outside the Stream provider, so the channel can't be activated
    // directly — navigating lets useChatChannelSync resolve and open it from the URL.
    // No `replace`: Back should return to the profile.
    onSuccess: ({ channelId }) => navigate(`${appUrl("chat")}/${channelId}`),
    // A blocked or unknown target answers NotFound, deliberately indistinguishable, so the
    // copy stays generic.
    onError: () => toast.error(t(RESOURCES.SendMessageError)),
  });

  const handleClick = () => {
    if (!isPhoneVerified) {
      setIsPhoneGateOpen(true);
      return;
    }
    createChannel({
      memberIdentityUserPublicIDs: [profile.identityUserPublicID],
      kind: ChatChannelKind.Direct,
      // A direct channel is the pair; it carries no membership rule.
      eligibility: ChatChannelEligibility.Anyone,
    });
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<ChatBubbleOutlineIcon />}
        disabled={isDisabled || isPending}
        onClick={guarded(handleClick)}
      >
        {t(RESOURCES.SendMessage)}
      </Button>
      {isPhoneGateOpen && (
        <PhoneVerificationDialog
          isOpen
          defaultPhone={phone ?? undefined}
          hasPhone={hasPhone}
          onClose={() => setIsPhoneGateOpen(false)}
          description={t(RESOURCES.SendMessagePhoneRequired)}
        />
      )}
    </>
  );
};
